/**
 * Whether an agent can still actually be working.
 *
 * One definition, shared by the orchestrator that stamps `UiAgent.liveness` and by the
 * attention builder that raises the orphan item, so the strip, the tiles, the capacity
 * summary and the inbox can never disagree about whether something is alive.
 *
 * The rule that matters: a run whose elapsed time is past the wall clock the runner itself
 * enforces cannot be running. The row is still `finished_at IS NULL`, so the database honestly
 * reports a live agent, and the card honestly renders `242:01:44 of 1h timeout` — a sentence
 * that is true and reads as fine. It is not fine. A ten-day-dead process presented as a live
 * agent is the difference between "the pipeline is busy" and "the pipeline lost a job last
 * week", and only one of those is worth waking up for.
 */

import type { AgentLiveness } from './types.ts';

/** The wall clock for a stage that has no entry in `STAGE_TIMEOUT_MS`. */
export const DEFAULT_ATTEMPT_TIMEOUT_MS = 60 * 60 * 1000;

/**
 * How long one attempt may run, by the stage it is running.
 *
 * One number for every stage was wrong in one direction only: an implementer builds, tests and
 * often fixes a review's findings on a machine shared with other agents, and a refiner reads a
 * ticket and writes a brief. An hour is generous for the second and, on 2026-09-09, was not
 * enough for the first -- attempts were killed with the code written and the result unreported,
 * and the ticket then paid for the whole stage again.
 *
 * Only Implementing moves. Nothing here shortens a stage: a timeout that fires early turns
 * finished work into a failure, which is the fault this whole change exists to reduce.
 *
 * Lives in core rather than the runner because three parties read it: the runner enforces it,
 * the orchestrator reaps against it, and the dashboard draws the budget ring and calls a run
 * orphaned past it. When only the runner knew the number, the other two judged a healthy
 * ninety-minute implementer against an hour and called it lost from minute 69.
 */
const STAGE_TIMEOUT_MS: Readonly<Record<string, number>> = {
  Implementing: 90 * 60 * 1000,
};

/**
 * The wall clock for an attempt in `state`.
 *
 * An explicitly configured timeout always wins, including when it is shorter: an operator who
 * set a number meant it, and a stage default that quietly overrode them would make the setting
 * a lie. Readers of a recorded attempt pass its `timeout_ms` here, so a row written by an
 * older runner that recorded none is judged against the stage default.
 */
export function stageTimeoutMs(state: string, configured: number | null | undefined): number {
  if (configured !== undefined && configured !== null) return configured;
  return STAGE_TIMEOUT_MS[state] ?? DEFAULT_ATTEMPT_TIMEOUT_MS;
}

/**
 * How far past its wall clock a run may be before it is called orphaned.
 *
 * A grace factor rather than a bare comparison: the runner's kill is not instantaneous, and a
 * browser's clock is not the orchestrator's.
 */
const ORPHAN_GRACE = 1.15;

/** How long a run may say nothing before the screen calls it quiet. */
const QUIET_AFTER_MS = 120_000;

export interface LivenessInput {
  startedAt: number;
  /** Runner-enforced wall clock, ms. */
  timeoutMs: number;
  /** Epoch ms of the last log chunk, or null when nothing has streamed yet. */
  lastLogAt: number | null;
}

/** True when the run has outlived the wall clock the runner enforces, grace included. */
export function isOrphaned(agent: LivenessInput, now: number): boolean {
  return now - agent.startedAt > agent.timeoutMs * ORPHAN_GRACE;
}

/** How far past its wall clock the run is, in ms. Zero when it is not past it. */
export function overrunBy(agent: LivenessInput, now: number): number {
  const elapsed = now - agent.startedAt;
  return elapsed > agent.timeoutMs ? elapsed - agent.timeoutMs : 0;
}

/**
 * Silence is measured from the last chunk, or from the start when nothing has ever arrived.
 *
 * That fallback is the point: treating a null `lastLogAt` as zero silence made a run that had
 * said nothing at all for half an hour the only card on screen showing no warning, while a
 * chatty run pausing for three minutes got one. Total silence is the most alarming case.
 */
function quietFor(agent: LivenessInput, now: number): number {
  return Math.max(0, now - (agent.lastLogAt ?? agent.startedAt));
}

export function agentLiveness(agent: LivenessInput, now: number): AgentLiveness {
  if (isOrphaned(agent, now)) return 'orphaned';
  return quietFor(agent, now) > QUIET_AFTER_MS ? 'silent' : 'running';
}

/**
 * A runner is presumed dead after this much silence. New hosts heartbeat every 10 s;
 * legacy generated configs used 30 s. Three legacy beats plus five seconds of request
 * jitter avoids false offline flapping while those hosts are upgraded.
 */
export const HEARTBEAT_STALE_MS = 95_000;

type RunnerOfflineReason = 'ok' | 'not-online' | 'stale' | 'never-beat';

export interface RunnerLivenessInput {
  status: string;
  lastHeartbeatAt: number | null;
}

export interface RunnerLiveness {
  /** Status is `online` and the heartbeat is inside the staleness window. */
  live: boolean;
  /** The beat itself is recent, regardless of the status column. */
  heartbeatFresh: boolean;
  reason: RunnerOfflineReason;
  /** Null when live. Names the condition that failed — never blames a fresh heartbeat. */
  message: string | null;
}

function beatAge(ms: number): string {
  const seconds = Math.max(0, Math.round(ms / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
}

/**
 * Whether a registered runner can still claim work.
 *
 * One definition, shared by the Ops tile, the queue rows and the nav footer, so a runner
 * whose beat is seconds old but whose status column is not `online` cannot render as dead
 * on one panel and alive on another. The message names the condition that actually failed.
 */
export function runnerLiveness(
  input: RunnerLivenessInput,
  now: number,
  staleAfterMs = HEARTBEAT_STALE_MS,
): RunnerLiveness {
  const beat = input.lastHeartbeatAt;
  const heartbeatFresh = beat !== null && beat > now - staleAfterMs;
  const registeredOnline = input.status === 'online';
  if (registeredOnline && heartbeatFresh) {
    return { live: true, heartbeatFresh: true, reason: 'ok', message: null };
  }
  if (!registeredOnline) {
    const status = input.status.trim() === '' ? 'unknown' : input.status;
    return {
      live: false,
      heartbeatFresh,
      reason: 'not-online',
      message: `registered but not online (status: ${status})`,
    };
  }
  if (beat === null) {
    return {
      live: false,
      heartbeatFresh: false,
      reason: 'never-beat',
      message: 'heartbeat stale — never sent a beat',
    };
  }
  return {
    live: false,
    heartbeatFresh: false,
    reason: 'stale',
    message: `heartbeat stale — last beat ${beatAge(now - beat)} ago`,
  };
}
